import type { IconName } from './chrome/icons';
import { CURSOR_ART } from './guides/cursorArt.mjs';
import type { Rail } from './tools/types';
import type { ToolId } from './useEditorCanvas';

/**
 * What the tool in hand is, for its cursor and its Option guide. `key` names the tool, or
 * `tool:mode` when a mode works differently — `cut:knife`, `text:area`.
 */
export interface ToolFeedback { key: string; name: string; detail: string; icon: IconName; tool: ToolId }
const BASE: Record<ToolId, [string, IconName, string]> = {
  select: ['Select', 'cursor', 'Click an object to select it. Drag to move it, or drag a corner to resize.'],
  rect: ['Rectangle', 'square', 'Drag from one corner to the other to draw a rectangle.'],
  ellipse: ['Ellipse', 'circle', 'Drag to draw an ellipse. Hold Shift for a circle.'],
  polygon: ['Polygon', 'polygon', 'Drag to draw a polygon. Set the number of sides in Properties.'],
  star: ['Star', 'star', 'Drag to draw a star. Adjust its points in Properties.'],
  line: ['Line', 'line', 'Drag between two points to draw a line.'],
  arc: ['Arc', 'arc', 'Drag to draw an arc. Adjust its bend in Properties.'],
  text: ['Text', 'text', 'Click to start typing, or choose Area type to draw a text box.'],
  pen: ['Pen', 'pen', 'Click to place points. Drag a point as you place it to create a curve.'],
  draw: ['Draw', 'draw', 'Drag to draw a path. Choose a drawing mode in Properties.'],
  liquify: ['Liquify', 'warp', 'Drag the brush over a shape to deform its outline.'],
  width: ['Width', 'width', 'Drag on a stroke to change its width at that point.'],
  cut: ['Cut', 'scissors', 'Choose Eraser, Scissors or Knife to remove or split paths.'],
  warp: ['Transform', 'transform-scale', 'Select an object, then drag its handles to transform it.'],
  slice: ['Slice', 'slice', 'Drag a box around an area to export it as its own file.'],
  builder: ['Shape builder', 'combine', 'Select overlapping shapes, then drag across regions to join them.'],
  blend: ['Blend', 'blend', 'Click one shape, then another to create intermediate shapes between them.'],
  measure: ['Measure', 'dimension', 'Drag between two points to measure the distance.'],
  cloner: ['Cloner', 'cloner', 'Select shapes and choose Cloner to make copies. Add an Effector to scatter, grow, twist or fade them.'],
  artboard: ['Artboard', 'artboard', 'Drag to draw an artboard, or click to place the selected preset.'],
  addon: ['Add-on', 'addons', 'Choose an add-on in the panel, then use its controls to apply it.'],
};

/** Read the actual selected mode, rather than guessing from the toolbar family. */
export function toolFeedback(tool: ToolId, rail: Rail | null | undefined): ToolFeedback {
  const [name, icon, detail] = BASE[tool];
  const base = { key: tool, name, icon, detail, tool };
  // These rails also contain settings/effectors, not just pointer modes.
  if (tool === 'blend' || tool === 'cloner' || tool === 'artboard' || tool === 'addon') return base;
  const mode = rail?.top?.kind === 'buttons' ? rail.top.items.find(item => item.pressed) : undefined;
  if (!mode || (tool === 'select' && mode.id === 'object')) return base;
  const [title, ...description] = mode.title.split(' — ');
  // A mode with no guide of its own is its tool's way of working (Point type, the Pen's
  // Draw path, the Rectangle among the shapes): it goes by the tool's key, so the cursor,
  // the guide and "already introduced" all agree on one name for it.
  const own = `${tool}:${mode.id}`;
  return { key: CURSOR_ART[own] ? own : tool, tool, name: title!, icon: mode.icon, detail: description.join(' — ') || detail };
}

// Pre-rendered bitmaps keep live SVG rasterisation out of Chrome's native cursor path.
// Use one native-size image, not image-set: the latter still flickers on the
// reported Chrome/macOS setup even with decoded assets and no style mutations.
// The art and each hotspot are in guides/cursorArt.mjs; `scripts/tool-cursors.mjs` bakes it.
const CURSORS = import.meta.glob('../assets/tool-cursors/*.png', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;
const cursorURL = (key: string) => CURSORS[`../assets/tool-cursors/${key.replace(':', '--')}.png`];
const cursorStyles = new Map<string, string>();
/** The cursor for a guide key: its own, else its tool's, else the arrow. */
export function toolCursor(key: string): string {
  const name = [key, key.split(':')[0]!, 'select'].find((one) => cursorURL(one) && CURSOR_ART[one])!;
  let style = cursorStyles.get(name);
  if (!style) {
    const [x, y] = CURSOR_ART[name]!.hotspot;
    style = `url("${cursorURL(name)}") ${x} ${y}, default`;
    cursorStyles.set(name, style);
  }
  return style;
}

// Retain the decoded images across tool switches. Loading is once per editor session,
// never on pointermove or during a canvas repaint.
let cursorImages: HTMLImageElement[] | undefined;
export function preloadToolCursors(): void {
  if (cursorImages || typeof Image === 'undefined') return;
  cursorImages = Object.values(CURSORS).map(url => {
    const image = new Image();
    image.src = url;
    void image.decode().catch(() => { /* CSS retains its standard cursor fallback. */ });
    return image;
  });
}

export interface GuideState { option: boolean; inside: boolean; buttons: number; suppressed: boolean }
export type GuideEvent = { type: 'move'; inside: boolean; buttons: number; option: boolean }
  | { type: 'option'; down: boolean } | { type: 'press' | 'reset' | 'dismiss' };
export const emptyGuide: GuideState = { option: false, inside: false, buttons: 0, suppressed: false };
export function guideState(state: GuideState, event: GuideEvent): GuideState {
  switch (event.type) {
    case 'move': return { ...state, inside: event.inside, buttons: event.buttons, option: event.option,
      suppressed: event.option && (state.suppressed || event.buttons !== 0) };
    case 'option': return { ...state, option: event.down, suppressed: event.down ? state.suppressed || state.buttons !== 0 : false };
    case 'press': return { ...state, buttons: 1, suppressed: true };
    case 'dismiss': return { ...state, suppressed: true };
    case 'reset': return { ...emptyGuide };
  }
}
export function guideVisible(state: GuideState): boolean {
  return state.option && state.inside && state.buttons === 0 && !state.suppressed;
}
export function guidePosition(x: number, y: number, width: number, height: number, viewportWidth: number, viewportHeight: number) {
  return {
    x: Math.max(8, Math.min(x + 22 + width > viewportWidth - 8 ? x - width - 18 : x + 22, viewportWidth - width - 8)),
    y: Math.max(8, Math.min(y + 24, viewportHeight - height - 8)),
  };
}
